import React, { useState, useEffect, useRef, cloneElement } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Popping from 'poppingjs';
import { PoppingResize, PoppingScroll } from 'poppingjs/plugins';
import { PoppingFlip } from 'poppingjs/modifiers';
import { container, backgroundPlacements, isReferable, is, DoneTypingEvent } from './Utils';
import { ChaperoneButton } from './ChaperoneButton';
import { useChaperone } from './Hooks';

const ChaperoneStepProps = {
	index: PropTypes.number,
	id: PropTypes.string,
	find: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	placement: PropTypes.oneOf([
		'top',
		'top-left',
		'top-right',
		'bottom',
		'bottom-left',
		'bottom-right',
		'left',
		'left-top',
		'left-bottom',
		'right',
		'right-top',
		'right-bottom',
	]),
	gesture: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
	allowNextOnClick: PropTypes.bool,
	allowNextOnType: PropTypes.bool,
	isDoneOnClick: PropTypes.bool,
	allowNext: PropTypes.bool,
	allowSkip: PropTypes.bool,
	allowBack: PropTypes.bool,
	isDone: PropTypes.bool,
	backToIndex: PropTypes.number,
	nextToIndex: PropTypes.number,
	propertyMutation: PropTypes.func,
	onNext: PropTypes.func,
	onBack: PropTypes.func,
	onSkip: PropTypes.func,
	onDone: PropTypes.func,
	onLoad: PropTypes.func,
	onUnLoad: PropTypes.func,
	background: PropTypes.shape({
		backdropStyle: PropTypes.object,
		highlightStyle: PropTypes.object,
		gestureStyle: PropTypes.object,
	}),
	autoScrollDelay: PropTypes.number,
	duration: PropTypes.number,
	navigation: PropTypes.number,
	hideEvidence: PropTypes.bool,
	neumorphism: PropTypes.bool,
	children: PropTypes.element.isRequired,
};

const ChaperoneStepDefault = {
	placement: 'bottom-right',
	allowNextOnClick: false,
	allowNextOnType: false,
	isDoneOnClick: false,
	allowNext: true,
	allowSkip: true,
	allowBack: true,
	isDone: false,
	background: {},
	autoScrollDelay: 150,
	duration: 200,
	hideEvidence: true,
	neumorphism: false,
};

export const ChaperoneStep = (properties) => {
	const {
		chaperone: { index, currentId, previousIndex },
		setChaperone,
	} = useChaperone();
	const [state, setState] = useState(false);

	const {
		index: step,
		id,
		find,
		message,
		placement,
		gesture,
		allowNextOnClick,
		allowNextOnType,
		isDoneOnClick,
		allowNext,
		allowSkip,
		allowBack,
		isDone,
		onNext,
		onBack,
		onSkip,
		onDone,
		onLoad,
		onUnLoad,
		backToIndex,
		nextToIndex,
		background,
		autoScrollDelay,
		duration,
		navigation,
		hideEvidence,
		neumorphism,
		children,
	} = properties.propertyMutation
		? properties.propertyMutation(
				{ currentId, index: index === -1 ? previousIndex : index },
				{ ...properties }
		  )
		: properties;
	const isSingleton = is.fnc(properties.propertyMutation);
	const isReferableChildren = isReferable(children);
	const matchedIndex = index === step && index > -1;
	const matchedcurrentId = id === currentId;
	const matchedRequirement = matchedIndex && matchedcurrentId;
	const childrenref = useRef(null);
	const dialogref = useRef(null);
	const gestureref = useRef(null);
	const hasDone = isDone || isDoneOnClick;
	const handlerBuilder = (indexer, eventHandler, extra = {}) => {
		return (e) => {
			e.stopPropagation();
			if (
				is.fnc(eventHandler) &&
				eventHandler({
					target: e.target,
					children: isReferableChildren ? childrenref.current : childrenref.current && childrenref.current.children[0],
					dialog: dialogref.current,
				}) === false
			)
				return;
			setChaperone((prev) => ({
				...prev,
				matched: false,
				index: indexer(prev.index),
				previousIndex: prev.index,
				...extra,
			}));
			if (e.target.closest('.chaperone-btns')) e.target.disabled = true;
		};
	};
	const nextHandler = handlerBuilder((index) => nextToIndex || ++index, onNext);
	const prevHandler = handlerBuilder((index) => backToIndex || --index, onBack);
	const skipHandler =
		allowSkip && !hasDone && handlerBuilder(() => -1, onSkip, { skip: true });
	const doneHandler =
		hasDone && handlerBuilder(() => -1, onDone, { done: true });
	const targetProps = {};

	if (allowNextOnType && !hasDone) {
		Object.assign(targetProps, DoneTypingEvent(nextHandler));
	}

	if (allowNextOnClick && !hasDone) {
		targetProps.onClick = nextHandler;
	}

	if (isDoneOnClick) {
		targetProps.onClick = doneHandler;
	}

	useEffect(() => {
		let delay;
		if (matchedRequirement) {
			setChaperone((prev) => ({ ...prev, matched: true }));
			setState(true);
		} else {
			delay = setTimeout(() => {
				setState(false);
			}, duration);
		}
		return () => clearTimeout(delay);
	}, [setChaperone, matchedRequirement, step, id, duration]);

	useEffect(() => {
		let isOriginal = false;
		const children = childrenref.current;
		const dialog = dialogref.current;
		const gesture = gestureref.current;

		if (!(children && dialog)) return;

		const target =
			(find &&
				(is.fnc(find)
					? find(children)
					: children.querySelector(find))) ||
			((isOriginal = !isReferableChildren), children);

		if (!target && is.dev) {
			throw new Error(
				`ChaperoneStep id="${currentId}" index="${index}" property "find" selector content is returning null or cannot find on DOM. carefully check your content`
			);
		}

		const BackPl = backgroundPlacements(
			target,
			dialog,
			gesture,
			matchedRequirement,
			autoScrollDelay,
			background,
			duration,
			isSingleton,
			isReferableChildren,
			isOriginal,
			children,
			targetProps
		);

		if (BackPl.isFailed) return;

		Array.from(dialog.querySelectorAll('.chaperone-btn')).forEach(
			(btn) => (btn.disabled = false)
		);

		const Pop = Popping.create(target, dialog, {
			placement,
			rotateArrow: 45,
			allowRootRelative: true,
			allowPersistent: true,
			modifiers: [PoppingFlip],
			plugins: [PoppingResize, PoppingScroll],
		});

		const loadsArg = { target, dialog, index, currentId };

		is.fnc(onLoad) && onLoad(loadsArg);

		return () => {
			Pop.destroy();
			BackPl.unmount();
			is.fnc(onUnLoad) && onUnLoad(loadsArg);
		};
	}, [
		currentId,
		index,
		onLoad,
		onUnLoad,
		background,
		isReferableChildren,
		matchedRequirement,
		autoScrollDelay,
		duration,
		dialogref,
		placement,
		isSingleton,
		gestureref,
		targetProps,
		find,
		childrenref,
		step,
		id,
	]);

	const childProps = {
		key: id,
		ref: childrenref,
		['data-chaperone-step']: matchedRequirement && index,
	};

	return (
		<>
			{isReferableChildren ? (
				cloneElement(children, childProps)
			) : state || !hideEvidence ? (
				<div {...childProps}>{children}</div>
			) : (
				children
			)}
			{state && (
				<>
					{gesture &&
						ReactDOM.createPortal(
							<div ref={gestureref} className='chaperone-gesture'> 
								{gesture && is.fnc(gesture)
									? gesture(childrenref)
									: gesture}
							</div>,
							container
						)}
					{ReactDOM.createPortal(
						<div
							ref={dialogref}
							className={`chaperone-dialog${
								neumorphism ? ' neumorphism' : ''
							}`}
							role='dialog'
							data-popping-placement={placement}>
							<div className='chaperone-message'>{message}</div>
							{!!navigation && (
								<div className='chaperone-nav'>
									{new Array(navigation).fill().map((value, idx) => (
										<button
											key={`bullet-${idx}`}
											type='button'
											data-index={idx + 1}
											className={`bullet ${idx === index ? 'fired' : ''}`}
											disabled={idx === index}
											onClick={() =>
												setChaperone((prev) => ({
													...prev,
													matched: false,
													index: idx,
												}))
											}></button>
									))}
								</div>
							)}
							<div className='chaperone-btns'>
								<div className='chaperone-group'>
									{allowSkip && !hasDone && (
										<ChaperoneButton
											label='skip'
											title='skip'
											onClick={skipHandler}
											style={{ marginRight: '5px' }}
										/>
									)} 
								</div>
								<div className='chaperone-group'>
									{step !== 0 && allowBack && (
										<ChaperoneButton
											label='back'
											title='back previous step'
											onClick={prevHandler}
										/>
									)}
									{allowNext && !hasDone && (
										<ChaperoneButton
											label='next'
											title='next step'
											onClick={nextHandler}
										/>
									)}
									{isDone && (
										<ChaperoneButton
											label='done'
											title='done'
											onClick={doneHandler}
											style={{ marginRight: '5px' }}
										/>
									)}
								</div>
							</div>
						</div>,
						container
					)}
				</>
			)}
		</>
	);
};

ChaperoneStep.propTypes = ChaperoneStepProps;

ChaperoneStep.defaultProps = ChaperoneStepDefault;

/* o = done, - pending, = = doubt
maybe
- customizable reference and buttons
- gestures drag, keyboard, etc. 
o make your own gestures
o navigation
= transition moving to another target with only one step
*/

// bugs
// o the handles only allow on the target ref
// o hideEvidence is bugy
